import { useCallback, useEffect, useRef, useState } from 'react';
import { shippingApi } from '../../api/shippingApi';
import {
  EMPTY_SHIPMENT_PLAN_DRAFT,
  SHIPMENT_PLAN_DETAIL_FIELD_NAMES,
  arePlPackageDraftsDirty,
  buildPlPackagePatch,
  buildSingleFieldPatch,
  draftDisplayValue,
  isSameDraftValue,
  rowToShipmentPlanDraft,
} from './shipmentPlanFieldUtils';

const PL_PACKAGE_FIELDS = new Set(['pl_qty', 'package_qty']);

export function useShipmentPlanFieldSave({
  formatDate,
  onPatchRow,
  onAfterShipmentDateSaved,
}) {
  const [draft, setDraft] = useState({ ...EMPTY_SHIPMENT_PLAN_DRAFT });
  const savedRef = useRef({ ...EMPTY_SHIPMENT_PLAN_DRAFT });
  const draftRef = useRef({ ...EMPTY_SHIPMENT_PLAN_DRAFT });
  const rowRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const enqueueSave = useCallback((task) => {
    const next = saveQueueRef.current.then(task, task);
    saveQueueRef.current = next.catch(() => {});
    return next;
  }, []);

  const initFromRow = useCallback((row) => {
    rowRef.current = row;
    const nextDraft = rowToShipmentPlanDraft(row, formatDate);
    savedRef.current = nextDraft;
    draftRef.current = nextDraft;
    setDraft(nextDraft);
  }, [formatDate]);

  const updateDraftField = useCallback((field, value) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'pl_qty' && value !== '' && value !== null && value !== undefined) {
        const n = parseInt(String(value), 10);
        if (!Number.isNaN(n) && n > 0) {
          next.package_qty = '';
        }
      }
      return next;
    });
  }, []);

  const rollbackDraftFields = useCallback((fields) => {
    setDraft((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        next[field] = savedRef.current[field];
      });
      draftRef.current = next;
      return next;
    });
  }, []);

  const persistPatch = useCallback(async (patch) => {
    const row = rowRef.current;
    if (!row || !patch || Object.keys(patch).length === 0) return true;

    try {
      await shippingApi.updateShipmentPlan(
        row.hacchu_code,
        row.hacchu_changecnt,
        row.hacchu_datacode,
        patch
      );

      const nextSaved = { ...savedRef.current };
      const nextDraft = { ...draftRef.current };
      Object.entries(patch).forEach(([field, value]) => {
        const display = draftDisplayValue(field, value);
        nextSaved[field] = display;
        nextDraft[field] = display;
      });
      savedRef.current = nextSaved;
      draftRef.current = nextDraft;
      setDraft(nextDraft);

      onPatchRow(patch, row);

      if (patch.shipment_date !== undefined && onAfterShipmentDateSaved) {
        await onAfterShipmentDateSaved({ ...row, ...patch });
      }
      return true;
    } catch (error) {
      console.error('発送予定保存エラー:', error);
      alert(error.message || '保存に失敗しました');
      return false;
    }
  }, [onAfterShipmentDateSaved, onPatchRow]);

  const saveFieldOnBlur = useCallback((field) => {
    return enqueueSave(async () => {
      const currentDraft = draftRef.current;
      const saved = savedRef.current;

      if (PL_PACKAGE_FIELDS.has(field)) {
        if (!arePlPackageDraftsDirty(currentDraft, saved)) return true;
        const patch = buildPlPackagePatch(currentDraft.pl_qty, currentDraft.package_qty);
        const ok = await persistPatch(patch);
        if (!ok) rollbackDraftFields(['pl_qty', 'package_qty']);
        return ok;
      }

      if (isSameDraftValue(field, currentDraft[field], saved[field])) {
        return true;
      }

      const patchResult = buildSingleFieldPatch(field, currentDraft[field]);
      if (patchResult.error) {
        alert(patchResult.error);
        rollbackDraftFields([field]);
        return false;
      }
      const ok = await persistPatch(patchResult);
      if (!ok) rollbackDraftFields([field]);
      return ok;
    });
  }, [enqueueSave, persistPatch, rollbackDraftFields]);

  const flushAllDirty = useCallback(() => {
    return enqueueSave(async () => {
      const currentDraft = draftRef.current;
      const saved = savedRef.current;

      if (arePlPackageDraftsDirty(currentDraft, saved)) {
        const patch = buildPlPackagePatch(currentDraft.pl_qty, currentDraft.package_qty);
        const ok = await persistPatch(patch);
        if (!ok) {
          rollbackDraftFields(['pl_qty', 'package_qty']);
          return false;
        }
      }

      for (const field of SHIPMENT_PLAN_DETAIL_FIELD_NAMES) {
        if (PL_PACKAGE_FIELDS.has(field)) continue;
        if (isSameDraftValue(field, draftRef.current[field], savedRef.current[field])) continue;

        const patchResult = buildSingleFieldPatch(field, draftRef.current[field]);
        if (patchResult.error) {
          alert(patchResult.error);
          rollbackDraftFields([field]);
          return false;
        }
        const ok = await persistPatch(patchResult);
        if (!ok) {
          rollbackDraftFields([field]);
          return false;
        }
      }
      return true;
    });
  }, [enqueueSave, persistPatch, rollbackDraftFields]);

  const handleDrawerClose = useCallback(async () => {
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    await new Promise((resolve) => setTimeout(resolve, 0));
    await flushAllDirty();
  }, [flushAllDirty]);

  return {
    draft,
    initFromRow,
    updateDraftField,
    saveFieldOnBlur,
    flushAllDirty,
    handleDrawerClose,
  };
}
